import {Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import * as moment from 'moment';
import {MenuItem, TreeNode} from 'primeng/api';
import {AuthGuard} from 'src/app/common/auth-guard.service';
import {ConsumptionData} from 'src/app/model/consumption-data';
import {ConsumptionFilters} from 'src/app/model/filters/consumption-filters';
import {Geocoding} from 'src/app/model/geocoding';
import {MachineModel} from 'src/app/model/machine-model';
import {AnalysisService} from 'src/app/services/analysis.service';
import {GeocodingService} from 'src/app/services/geocoding.service';
import {HierarchyService} from 'src/app/services/hierarchy.service';
import {MachineModelsService} from 'src/app/services/machine-models.service';
import {MachinesService} from "../../../services/machines.service";
import {Machine} from "../../../model/machine";
import {ColorService} from "../../../services/color.service";
import {GenericGraphComponent} from "../../shared/generic-graph/generic-graph.component";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {TimezoneService} from "../../../services/timezone.service";
import {CommonTranslationsService} from "../../../services/common-translations.service";
import {Measure} from "../../../model/measure";
import {ConsumptionDataResponse} from "../../../model/responses/consumption-data-response";
import {ExportFileType} from "../../../model/export-file-type";

@Component({
  selector: 'app-analysis-consumption',
  templateUrl: './analysis-consumption.component.html',
  styleUrls: ['./analysis-consumption.component.scss']
})
export class AnalysisConsumptionComponent implements OnInit {

  @ViewChild('totalSqueezedFruitsChart')
  public totalSqueezedFruitsChart: GenericGraphComponent;

  public machines: Machine[] = [];
  public hierarchy: TreeNode[];
  public organizations: TreeNode[];
  public models: MachineModel[];
  public countries: { name: string }[];

  public isStateDisabled: boolean;
  public isLocalityDisabled: boolean;

  public graphMeasures: any[];
  public graphAxis: { labels: { format: string; }; title: { enabled: boolean; }; }[];

  public isDataLoading: boolean = true;

  public tempPeriodRange: [any, any];
  public specificPeriodRange: [any, any] = [null, null];
  public filtersVisible: boolean;
  public availableYearRange: string;

  public consumptionData: ConsumptionData;

  public squeezedFruitsTranslation: string;
  public squeezedFruitsByHourOfDay: { categories: string[], data: { name: string, data: number[] }[] };
  public squeezedFruitsByDayOfWeek: { categories: string[], data: { name: string, data: number[] }[] };
  public squeezedFruitsByMonthOfYear: { categories: string[], data: { name: string, data: number[] }[] };

  public plotOptions: any;
  public squeezedFruitsByHourOfDayDivWidth: number;
  public squeezedFruitsByDayOfWeekDivWidth: number;
  public squeezedFruitsByMonthOfYearDivWidth: number;

  public updateFlag: boolean = false;

  public currentFilters: ConsumptionFilters;
  public firstShow: boolean;
  public language: string;

  public exportVisible: boolean;
  public chartActions: MenuItem[];
  public exportFileType: ExportFileType;

  private colorMap: Map<string, string>;

  private states: { name: string }[];
  private localities: { name: string }[];
  private geocodings: Geocoding[];
  private countryStates: Map<string, Set<string>>;
  private stateLocalities: Map<string, Set<string>>;

  private periodRange: [number, number] = [null, null];
  private hoursOfDay: string[] = [];
  private daysOfWeek: string[] = [];
  private months: string[] = [];

  private serialTranslation: string;
  private totalTranslation: string;
  private hoursTranslation: string;

  private isPeriodRangeValid: boolean = false;
  private resetFilterAction: boolean;
  private exportChart;


  constructor(
    private readonly analysisService: AnalysisService,
    private readonly i18n: TranslocoService,
    private readonly hierarchyService: HierarchyService,
    private readonly geocodingService: GeocodingService,
    private readonly machineModelsService: MachineModelsService,
    private readonly machinesService: MachinesService,
    private readonly timezoneService: TimezoneService,
    private readonly colorService: ColorService,
    public authGuard: AuthGuard,
    private commonMessagesService: CommonMessagesService,
    private commonTranslationsService: CommonTranslationsService
  ) {
    this.hoursOfDay = this.timezoneService.getHoursOfDay();
    this.currentFilters = new ConsumptionFilters();
    this.firstShow = true;
    this.chartActions = [];
  }

  ngOnInit(): void {
    this.language = this.i18n.getActiveLang();

    this.availableYearRange = this.timezoneService.calcYearRange();
    this.i18n.selectTranslation().subscribe(() => {
      this.squeezedFruitsTranslation = this.i18n.translate('misc.overview.squeezedFruits');
      this.serialTranslation = this.i18n.translate('attribute.serialNumber');
      this.totalTranslation = this.i18n.translate('attribute.total');
      this.hoursTranslation = this.i18n.translate('time.hours:short');
      this.squeezedFruitsTranslation = this.i18n.translate('misc.overview.squeezedFruits');
      this.hoursOfDay = this.hoursOfDay.map(h => h + this.hoursTranslation);
      this.buildChartActions();
    });
    this.setPlotOptions();
    this.getMachineConsumption();
    this.loadFilters();
  }

  private buildChartActions(): void {
    this.chartActions = [
      {label: this.i18n.translate('common.exportXls'), icon: 'pi pi-file-excel', command: () => this.showExportToXls()},
      {label: this.i18n.translate('common.downloadPng'), icon: 'pi pi-download', command: () => this.downloadChart()}
    ];
  }

  showExportToXls(): void {
    this.exportVisible = true;
  }

  downloadChart(): void {
    try {
      setTimeout(() => {
        if (this.exportFileType === ExportFileType.ANALYSIS_CONSUMPTION_AND_WORK_CHART) {
          if (this.totalSqueezedFruitsChart.chart.axes != undefined) {
            this.exportChart = this.totalSqueezedFruitsChart.chart;
          }
          this.totalSqueezedFruitsChart.download(this.exportChart);
        }
      });
    } catch (ignored) {
    }
  }

  private getMachineConsumption(): void {
    this.isDataLoading = true;
    if (this.firstShow) {
      this.consumptionData = null;
      this.graphMeasures = [];
      this.isDataLoading = false;

    } else {
      this.analysisService.getMachineConsumption(this.currentFilters).subscribe((response: ConsumptionDataResponse) => {
        if (response.consumptionData != null) {
          this.consumptionData = response.consumptionData;

          this.commonTranslationsService.getDaysOfWeekAndMonthsOfYear().subscribe((responseTranslations) => {
            this.daysOfWeek = responseTranslations.daysOfWeek;
            this.months = responseTranslations.monthsOfYear;

            let totalFruitsMap: Map<string, number> = new Map<string, number>();
            let graphMeasures: any[] = [];

            Object.entries(this.consumptionData.measures).forEach((entry: [string, Measure[]]) => {
              const result: number = entry[1].reduce((accumulator: number, measure: Measure) => {
                return accumulator + parseInt(measure.value);
              }, 0);
              totalFruitsMap.set(entry[0], result);
            });

            const mapSorted: Map<string, number> = new Map([...totalFruitsMap.entries()].sort((a, b) => b[1] - a[1]));
            this.colorMap = this.colorService.getColorMap(mapSorted);

            Object.entries(this.consumptionData.measures).forEach((entry: [string, Measure[]]) => {
              graphMeasures.push({
                name: entry[0],
                data: entry[1].map((measure: Measure): [number, number] => {
                  return [moment(measure.timestamp).utc().valueOf(), parseFloat(measure.value)]
                }),
                color: this.colorMap.get(entry[0]),
                yAxis: 0
              })
            });

            this.graphAxis = [{labels: {format: '{value}'}, title: {enabled: false}}];
            this.graphMeasures = graphMeasures;

            this.squeezedFruitsByHourOfDay = this.parseMap(
              this.consumptionData.squeezedFruitsByHourOfDay,
              this.hoursOfDay,
              0
            );
            this.squeezedFruitsByDayOfWeek = this.parseMap(
              this.consumptionData.squeezedFruitsByDayOfWeek,
              this.daysOfWeek, 0, 1
            );
            this.squeezedFruitsByMonthOfYear = this.parseMap(
              this.consumptionData.squeezedFruitsByMonthOfYear,
              this.months, 0, 1
            );

            this.isDataLoading = false;

          });

        } else {
          this.isDataLoading = false;
        }
      }, () => {
        this.commonMessagesService.showElasticSearchMeasuresError();
      });
    }
  }

  @ViewChild('squeezedFruitsByHourOfDayDiv', {static: true}) squeezedFruitsByHourOfDayDiv: ElementRef;
  @ViewChild('squeezedFruitsByDayOfWeekDiv', {static: true}) squeezedFruitsByDayOfWeekDiv: ElementRef;
  @ViewChild('squeezedFruitsByMonthOfYearDiv', {static: true}) squeezedFruitsByMonthOfYearDiv: ElementRef;

  ngAfterViewInit(): void {
    this.squeezedFruitsByHourOfDayDivWidth = this.squeezedFruitsByHourOfDayDiv.nativeElement.offsetWidth;
    this.squeezedFruitsByDayOfWeekDivWidth = this.squeezedFruitsByDayOfWeekDiv.nativeElement.offsetWidth;
    this.squeezedFruitsByMonthOfYearDivWidth = this.squeezedFruitsByMonthOfYearDiv.nativeElement.offsetWidth;
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.squeezedFruitsByHourOfDayDivWidth = this.squeezedFruitsByHourOfDayDiv.nativeElement.offsetWidth;
    this.squeezedFruitsByDayOfWeekDivWidth = this.squeezedFruitsByDayOfWeekDiv.nativeElement.offsetWidth;
    this.squeezedFruitsByMonthOfYearDivWidth = this.squeezedFruitsByMonthOfYearDiv.nativeElement.offsetWidth;
    this.updateFlag = true;
  }

  private setPlotOptions(): void {
    this.plotOptions = {
      series: {
        color: getComputedStyle(document.documentElement).getPropertyValue('--primary-color')
      },
      column: {
        stacking: 'normal',
        dataLabels: {enabled: false}
      }
    }
  }

  private getPeriodRange(periodRangeValid: boolean, periodRange?: [any, any]): [number, number] {
    return this.timezoneService.getPeriodRange('specificPeriod', periodRangeValid, periodRange);
  }

  private parseMap(object: any, categories: string[], slicePositions: number = 0, initIndex: number = 0): {
    categories: string[],
    data: any
  } {
    if (object != null) {
      const map: Map<string, number[]> = this.getSerialMeasuresMap(object, categories, slicePositions, initIndex);

      const size: number = map.size;
      if (size > 0) {
        return {
          categories: this.sliceArrayToLeft<string>(categories, slicePositions),
          data: this.getData(map)
        }
      }
    }

    return {
      categories: categories,
      data: null
    };
  }

  private getSerialMeasuresMap(object: any, categories: string[], slicePositions: number = 0, initIndex: number = 0): Map<string, number[]> {
    let map: Map<string, number[]> = new Map<string, number[]>();

    if (object != null) {
      const times: string[] = Object.keys(object);
      const length: number = categories.length;

      for (let time of times) {
        const measures = object[time];
        const serials: string[] = Object.keys(measures);
        for (let serial of serials) {
          let fruits = measures[serial];
          let measuresForTimeAndSerial: number[] = map.get(serial);
          if (measuresForTimeAndSerial == null) {
            measuresForTimeAndSerial = Array.from({length}, (_, i) => 0);
          }
          let index: number = (parseInt(time) - initIndex) - slicePositions;
          if (index < 0) {
            index = length + index;
          }
          measuresForTimeAndSerial[index] = fruits;
          map.set(serial, measuresForTimeAndSerial);
        }
      }
    }

    return map;
  }

  private getData(map: Map<string, number[]>): any {
    const dataSeries: any[] = [];
    map.forEach((values: number[], serial: string) => {
      if (this.colorMap.has(serial)) {
        dataSeries.push({name: serial, data: values, color: this.colorMap.get(serial)});
      } else {
        dataSeries.push({name: serial, data: values});
      }
    });
    return dataSeries;
  }

  private sliceArrayToLeft<T>(array: T[], n: number): T[] {
    const position = n % array.length;
    return array.slice(position).concat(array.slice(0, position));
  }

  public tooltipFormatter(): string {
    return `${this.serialTranslation} "{series.name}": {point.y:,.0f}<br/>${this.totalTranslation}: {point.stackTotal:,.0f}`
  }

  public tooltipFormatterChart(unit: string): string {
    return `${this.serialTranslation} "{series.name}"<br/>{point.y:,.0f} ${unit}`
  }

  private loadFilters(): void {
    this.buildTreeNode();
    this.machineModelsService.getModels().subscribe(response => {
      this.models = response.models;
    });
    this.loadGeocoding();
    this.machinesService.getMachinesMinSummary().subscribe(response => {
      this.machines = response.machines;
    });
  }

  private loadGeocoding(): void {
    this.isStateDisabled = true;
    this.isLocalityDisabled = true;
    this.countryStates = new Map<string, Set<string>>();
    this.stateLocalities = new Map<string, Set<string>>();
    this.geocodingService.getMachineGeocodings(this.language).subscribe(response => {
      this.geocodings = response.geocodings;
      let geocodingData = this.geocodingService.getGeocodingData(this.geocodings, this.language);
      this.countryStates = geocodingData.countryStates;
      this.stateLocalities = geocodingData.stateLocalities;
      this.countries = geocodingData.countries;
      this.states = geocodingData.states;
      this.localities = geocodingData.localities;
    });
  }

  private buildTreeNode(): void {
    this.hierarchyService.getHierarchyDropdown().subscribe(response => {
      this.hierarchy = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto)[1];
    });
  }

  public mapProperty(array: any[], property: string): any[] {
    return array.map(i => i[property]);
  }

  onCountryChange(event): void {
    this.isStateDisabled = !(event.value && event.value.length > 0);
    this.isLocalityDisabled = true;
    this.currentFilters.states = [];
    this.currentFilters.localities = [];
  }

  onStateChange(event): void {
    this.isLocalityDisabled = !(event.value && event.value.length > 0);
    this.currentFilters.localities = [];
  }

  get filteredStates(): { name: string }[] {
    if (this.currentFilters.countries?.length > 0) {
      return this.states.filter(state => {
        for (const country of this.currentFilters.countries) {
          if (this.countryStates.get(country).has(state.name)) {
            return true;
          }
        }
        return false;
      });
    }
    return this.states;
  }

  get filteredLocalities(): { name: string }[] {
    if (this.currentFilters.states?.length > 0) {
      return this.localities.filter(locality => {
        for (const state of this.currentFilters.states) {
          if (this.stateLocalities.get(state).has(locality.name)) {
            return true;
          }
        }
        return false;
      });
    }
    return this.localities;
  }

  resetFilters(): void {
    this.currentFilters = new ConsumptionFilters();
    this.periodRange = [null, null];
    this.specificPeriodRange = [null, null];
    this.tempPeriodRange = null;
    this.currentFilters.from = null;
    this.currentFilters.until = null;
    this.organizations = [];
    this.filtersVisible = true;
    this.firstShow = true;
    this.resetFilterAction = true;
  }

  applyFilters(): void {
    if (this.resetFilterAction) {
      this.resetFilterAction = false;
    } else {
      this.firstShow = false;
    }
    this.currentFilters.organizationUuids = [];
    if (this.organizations != null && this.organizations.length > 0) {
      this.organizations.forEach(organization => {
        this.currentFilters.organizationUuids.push(organization.data);
      });
    }
    this.getMachineConsumption();
  }

  public getContentExport(): any[] {
    if (this.exportFileType === ExportFileType.ANALYSIS_CONSUMPTION_AND_WORK_CHART) {
      return this.graphMeasures;
    } else {
      return [this.periodRange, this.consumptionData, this.squeezedFruitsByHourOfDay, this.squeezedFruitsByDayOfWeek,
        this.squeezedFruitsByMonthOfYear, this.graphMeasures];
    }
  }

  public validatePeriodRange(periodRange: [any, any]): void {
    this.tempPeriodRange = periodRange;
    this.isPeriodRangeValid = periodRange?.[0] != null && periodRange?.[1] != null;

    this.specificPeriodRange[0] = this.tempPeriodRange[0];
    this.specificPeriodRange[1] = this.tempPeriodRange[1];
    this.periodRange = this.getPeriodRange(this.isPeriodRangeValid, periodRange);

    this.currentFilters.from = this.periodRange[0];
    this.currentFilters.until = this.periodRange[1];
  }

  public readonly ExportFileType = ExportFileType;
}

